import {
	PropsWithChildren,
	Children,
	ReactElement,
	useState,
	useCallback,
	useEffect
} from 'react';
import styles from './PagedDialog.module.scss';
import {
	Modal,
	H1,
	Text,
	Button,
	Loader
} from '@components';
import { PagedDialogContext } from './Context';
import { Footer } from './Footer';
import { Menu } from './Menu';
import { MenuItem } from './MenuItem';
import { Preview } from './Preview';
import { Pages } from './Pages';
import { Page } from './PageItem';

type TPagedDialogSize = 'default' | 'large';

type TProps = {
	title: string | JSX.Element;
	description: string | JSX.Element;
	open: boolean;
	toggleOpen: (b: boolean) => void;
	showCloseButton?: boolean;
	closeOnEscape?: boolean;
	closeOnClickOutside?: boolean;
	defaultStartID?: number;
	loading?: boolean;
	hideFooter?: boolean;
	contentHeight?: string;
	size?: TPagedDialogSize;
}

const PagedDialogMain = (props: PropsWithChildren<TProps>) => {
	const {
		title,
		description,
		open,
		toggleOpen,
		showCloseButton,
		closeOnEscape,
		closeOnClickOutside,
		loading,
		hideFooter = false,
		contentHeight = '500px',
		size = 'default',
		children
	} = props;

	const getRelevantChildren = useCallback((type: 'menu' | 'pages' | 'preview') => {
		const menuElements: ReactElement[] = [];
		const pageElements: ReactElement[] = [];
		const previewElements: ReactElement[] = [];
		Children.toArray(children).forEach((Child) => {
			// @ts-ignore
			if (typeof (Child as ReactElement<any>).type === 'function' && ((Child as ReactElement<any>).type.name === 'Menu' || (Child as ReactElement<any>).type.displayName === 'Menu')) {
				menuElements.push(Child as ReactElement<any>);
			// @ts-ignore
			} else if (typeof (Child as ReactElement<any>).type === 'function' && ((Child as ReactElement<any>).type.name === 'Preview' || (Child as ReactElement<any>).type.displayName === 'Preview')) {
				previewElements.push(Child as ReactElement<any>);
			// @ts-ignore
			} else if (typeof (Child as ReactElement<any>).type === 'function' && ((Child as ReactElement<any>).type.name === 'Pages' || (Child as ReactElement<any>).type.displayName === 'Pages')) {
				pageElements.push(Child as ReactElement<any>);
			}
		});
		switch (type) {
		case 'menu':
			return menuElements;
		case 'preview':
			return previewElements;
		default:
			return pageElements;
		}
	}, [children, loading]);

	const generateFooter = () => {
		let footer = null;
		Children.toArray(children).forEach((child) => {
			// @ts-ignore
			if (typeof (child as ReactElement<any>).type === 'function' && ((child as ReactElement<any>).type.name === 'Footer' || (child as ReactElement<any>).type.displayName === 'Footer')) {
				footer = child;
			}
		});
		if (!footer && !hideFooter) {
			return <Footer>
				<div className={styles.defaultFooterContent} data-testid={'@ofload-paged-dialog-footer'}>
					<Button text={'Close'} onClick={() => toggleOpen(false)} />
				</div>
			</Footer>
		}
		return footer;
	}

	const loader = () => {
		return <div className={styles.loader} data-testid={'@ofload-paged-dialog-loader'}>
			<Loader />
		</div>
	}

	return (<Modal
		open={open}
		toggleOpen={toggleOpen}
		showCloseButton={showCloseButton}
		closeOnEscape={closeOnEscape}
		closeOnClickOutside={closeOnClickOutside}
		padding={0}
	>
		<div className={`${styles.mainContainer} ${size === 'large' ? styles.large : ''}`}>
			<div className={styles.header}>
				<H1>{title}</H1>
				<Text>{description}</Text>
			</div>
			<div className={styles.modalForm} style={size === 'large' ? undefined : { height: contentHeight }} data-testid={'@Ofload-PagedDialog-Form'}>
				{loading && loader()}
				{!loading && <>
					{getRelevantChildren('menu')}
					{getRelevantChildren('pages')}
					{getRelevantChildren('preview')}
				</>}
			</div>
			{generateFooter()}
		</div>
	</Modal>);
}

const _PagedDialog = (props: PropsWithChildren<TProps>) => {
	const { defaultStartID, open, loading } = props;
	const [activeMenu, setActiveMenu] = useState(defaultStartID ?? 0);
	const [activeSubMenu, setActiveSubMenu] = useState(0);
	const [defaultContext, setDefaultContext] = useState({
		activeMenu,
		setActiveMenu,
		activeSubMenu,
		setActiveSubMenu
	});
	useEffect(() => {
		setDefaultContext({
			activeMenu,
			setActiveMenu,
			activeSubMenu,
			setActiveSubMenu
		});
	}, [activeMenu, activeSubMenu, open]);
	useEffect(() => {
		if (!loading) {
			setActiveMenu(defaultStartID ?? 0);
			setActiveSubMenu(0);
		}
	}, [loading, defaultStartID]);
	return (
		<PagedDialogContext.Provider value={defaultContext}>
			<PagedDialogMain {...props} />
		</PagedDialogContext.Provider>
	)
}

_PagedDialog.Footer = Footer;
_PagedDialog.Menu = Menu;
_PagedDialog.MenuItem = MenuItem;
_PagedDialog.Pages = Pages;
_PagedDialog.Page = Page;
_PagedDialog.Preview = Preview;
export const PagedDialog = _PagedDialog;
